import { asText } from '@prismicio/helpers';
import { PrismicRichText } from '@prismicio/react';
import classNames from 'classnames';
import { gsap } from 'gsap';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import tailwindConfig from '../../../tailwind.config';
import {
  PageBodyBlock_Hero_FormFields,
  PageBodyBlock_Hero_FormPrimary,
} from '../../../types/prismic';
import { media } from '../../styles/media';
import getButtonVariant from '../../utils/getButtonVariant';
import { resolveColorCK } from '../../utils/resolveColorCK';
import FormResolver from '../forms/FormResolver';
import Play from '../icons/Play';
import { Button } from '../ui';
import ModalVideo from '../ui/ModalVideo';
import ScrollIndicator from '../ui/ScrollIndicator';

const Section = styled.section<{ color: string }>`
  position: relative;
  overflow: hidden;
  opacity: 0;

  p a {
    text-decoration: underline;
  }
  .referral_name {
    display: none;
    &.active {
      display: block;
    }
  }
  .bg--hero--holder {
    position: relative;
    z-index: -1;
    width: 100%;
    height: 225px;
    overflow: hidden;
    background: var(--primary);
    &.primary-dark {
      background: var(--primary);
    }
    @media ${media.mdUp} {
      height: 480px;
      margin: -100px 0 0;
      padding-inline-start: 0;
    }
    @media ${media.lgUp} {
      height: 660px;
    }
  }
  .bg--hero {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    background: var(--grey-2) no-repeat;
    background-position: bottom;
    background-size: cover;
    overflow: hidden;
    @media ${media.mdUp} {
      background-position: bottom center;
    }
  }
  .bg--right {
    position: relative;
    &::before {
      display: none;
      position: absolute;
      z-index: 1;
      content: '';
      bottom: 0;
      inset-inline-end: -100%;
      height: 100%;
      width: 100%;
      background: ${({ color }) =>
        tailwindConfig.theme.colors[resolveColorCK(color)]};

      @media ${media.mdUp} {
        display: block;
      }
      @media ${media.lgUp} {
        display: none;
      }
    }
    &.bg--primary-dark::before {
      background: var(--primary);
    }
  }
  .form--holder {
    @media ${media.mdDown} {
      margin: 0 -36px;
      padding: 32px 36px 60px;
    }
    @media ${media.xsDown} {
      margin: 0 -16px;
      padding: 32px 16px 60px;
    }
  }
  .bg--left {
    &::before {
      position: absolute;
      z-index: -1;
      content: '';
      bottom: 0;
      inset-inline-start: -50%;
      height: 100%;
      width: 100%;
      background: var(--grey-1);
      @media ${media.xlUp} {
        inset-inline-start: -98%;
      }
    }
    hr {
      border-top: 1px solid var(--grey-3);
    }
  }
`;

const iconNext = (
  <svg viewBox="0 0 20 17" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M11.25 0L9.562 1.636l5.782 5.606H0v2.546h15.344l-5.75 5.576L11.28 17 20 8.515 11.25 0z"
      fill="#FCFCF7"
    />
  </svg>
);

const iconPrev = (
  <svg viewBox="0 0 20 17" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M8.75 17l1.688-1.636-5.782-5.606H20V7.212H4.656l5.75-5.576L8.72 0 0 8.485 8.75 17z"
      fill="#FCFCF7"
    />
  </svg>
);

interface BlockHeroFormSliceProps {
  slice: {
    primary: PageBodyBlock_Hero_FormPrimary;
    items: PageBodyBlock_Hero_FormFields[];
  };
}

const BlockHeroFormSlice: React.FunctionComponent<BlockHeroFormSliceProps> = (
  props
) => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [active, setActive] = useState(false);
  let ctx;

  useEffect(() => {
    ctx = gsap.context(() => {
      const fadeElements = gsap.timeline();
      fadeElements.to('#contact', {
        opacity: 1,
        delay: 0.25,
      });
      fadeElements.from('#contact .fade-in-1', {
        duration: 0.6,
        opacity: '0',
        y: '5%',
      });
      fadeElements.from(
        '#contact .bg--hero--holder',
        {
          duration: 0.6,
          opacity: '0',
        },
        '-=0.4'
      );
      // fadeElements.from(
      //   '#contact .fade-up',
      //   {
      //     duration: 0.7,
      //     opacity: '0',
      //     y: '20%',
      //     stagger: {
      //       amount: 0.5,
      //     },
      //   },
      //   '-=0.6'
      // );

      const bgTL = gsap.timeline({
        scrollTrigger: {
          trigger: '#contact',
          scrub: 1,
          start: 'top 90%',
        },
      });

      bgTL.fromTo('.bg--hero', { scale: 1.1 }, { scale: 1, force3D: true }, 0);
    });

    return () => {
      ctx?.revert();
    };
  }, []);

  const getBackgroundImages = (background, items) => {
    const backgroundImages = [] as string[];
    if (background?.url) {
      backgroundImages.push(background?.url);
    }
    items.map((item) => {
      const { carousel_image } = item;
      if (carousel_image?.url) {
        backgroundImages.push(carousel_image?.url);
      }
    });
    return backgroundImages;
  };

  const { slice } = props;
  const { primary, items } = slice;
  const {
    button_doc,
    cta_button_text,
    cta_button_link,
    heading,
    color,
    form_variant,
    description,
    button,
    background,
    video,
    video_button_text,
  } = primary;

  const backgroundImages = getBackgroundImages(background, items);

  const fadeInBg = () => {
    const fadeElements = gsap.timeline();
    fadeElements.from('#contact .bg--hero', {
      duration: 0.6,
      opacity: 0.1,
      y: '-5%',
    });
    ctx?.add(fadeElements);
  };

  const handleChangePrev = () => {
    setActiveSlide((activeSlide) =>
      activeSlide === 0 ? backgroundImages.length - 1 : activeSlide - 1
    );
    fadeInBg();
  };

  const handleChangeNext = () => {
    setActiveSlide((activeSlide) =>
      activeSlide === backgroundImages.length - 1 ? 0 : activeSlide + 1
    );
    fadeInBg();
  };

  return (
    <Section color={color as string} id="contact" className="relative">
      <div className="container">
        <div className="grid sm:grid-cols-4 md:grid-cols-8 lg:grid-cols-12">
          <div className="relative col-span-6 pb-20 pt-20 md:col-span-3 md:col-start-1 lg:col-span-5 lg:pb-40 lg:pt-32">
            <h1
              className={classNames(
                'h2 fade-up mb-10 md:mb-12 md:pr-10 lg:pr-0',
                `text-${resolveColorCK(color)}`
              )}>
              {asText(heading.richText)}
            </h1>
            <div className="w-450 fade-up mb-12">
              <PrismicRichText field={description.richText} />
            </div>
            <div className={`fade-up flex flex-wrap items-center gap-6`}>
              <Button
                buttonDoc={button_doc}
                globalTypeform
                variant={getButtonVariant(color)}
                href={cta_button_link?.url}>
                {cta_button_text?.text}
              </Button>
              {video?.url && (
                <button
                  onClick={() => setActive(true)}
                  className={`btn--text--play text-${resolveColorCK(
                    color
                  )} whitespace-nowrap bg-[transparent]`}>
                  {video_button_text || 'How it Works'}
                  <span className="rtl:-translate-x-2 rtl:rotate-180 rtl:[&_svg]:-translate-x-[6px]">
                    <Play />
                  </span>
                </button>
              )}
            </div>
          </div>
          <div
            className={`col-span-8 md:col-span-4 md:col-start-4 lg:col-span-6 lg:col-start-7 bg-${resolveColorCK(
              color
            )} form--holder fade-in-1 bg--right mt-12 sm:ml-4 md:mt-0 lg:mt-12`}>
            <div className="md:pb-10 md:pl-10 md:pt-10 lg:p-16">
              <FormResolver
                caller="BlockHeroFormSlice"
                color={resolveColorCK(color)}
                buttonText={button.text}
                formVariant={form_variant}
              />
            </div>
          </div>

          <ScrollIndicator />
        </div>
      </div>

      <div className={`bg-${resolveColorCK(color)} bg--hero--holder`}>
        <div
          className="bg--hero"
          style={{
            backgroundImage: `url(${backgroundImages[activeSlide]})`,
          }}
        />
      </div>

      {backgroundImages.length > 1 && (
        <div className="absolute bottom-0 right-0 flex">
          <a
            className={`btn--box color--white btn--bg bg-${resolveColorCK(
              color
            )} relative z-10`}
            onClick={handleChangePrev}>
            {iconPrev}
          </a>
          <a
            className={`btn--box color--white btn--bg bg-${resolveColorCK(
              color
            )}`}
            onClick={handleChangeNext}>
            {iconNext}
          </a>
        </div>
      )}
      {video?.url && (
        <ModalVideo
          videoSrc={video?.url}
          open={active}
          onClose={() => setActive(false)}
        />
      )}
    </Section>
  );
};

export default BlockHeroFormSlice;
